import { ethers } from 'ethers';
import { formatNumber } from './commonFunction';
import { fetchLockedPayments } from './cpgDiamondReader';
import {
    getRemainingEscrowWei,
    isLockedPaymentDisputed,
    isLockedPaymentFinished,
} from './lockedPaymentClaimable';

export const mapLockedPaymentToHistoryRow = (paymentData, chainIndex, tokenInfo) => {
    if (!tokenInfo || !paymentData) return null;

    const { name, decimals, logoURI } = tokenInfo;
    const paymentId = Number(paymentData.paymentId);
    if (!Number.isFinite(paymentId)) return null;

    const tokenAddress = paymentData.tokenAddress ?? paymentData.token;
    const totalAmountFloat = parseFloat(
        ethers.formatUnits(paymentData.totalAmount, decimals),
    );
    const remainingEscrowFloat = parseFloat(
        ethers.formatUnits(getRemainingEscrowWei(paymentData), decimals),
    );
    const startTime = Number(paymentData.startTime ?? 0);
    const finished = isLockedPaymentFinished(paymentData);
    const disputed = isLockedPaymentDisputed(paymentData);

    return {
        ...paymentData,
        id: paymentId,
        paymentId,
        chainIndex,
        tokenAddress,
        tokenName: name,
        tokenImage: logoURI,
        tokenDecimal: decimals,
        amount: formatNumber(totalAmountFloat),
        parsedNumber: totalAmountFloat,
        remainingEscrowFloat,
        type: 'Milestone',
        paymentTime: startTime,
        isClaimed: finished,
        isDisputeRaised: disputed,
        milestonesNum: Number(paymentData.milestones || 0),
        claimedMilestonesNum: Number(paymentData.claimedMilestones || 0),
    };
};

/** Sum unique active milestone escrows (locked + disputed, not yet fully claimed). */
export const aggregateInEscrowMetrics = (rows = []) => {
    const byId = new Map();
    rows
        .filter((row) => String(row?.type || '').toLowerCase() === 'milestone')
        .forEach((row) => {
            const id = Number(row.id ?? row.paymentId);
            if (Number.isFinite(id)) {
                byId.set(id, row);
            }
        });

    let totalAmount = 0;
    let paymentCount = 0;
    let disputedCount = 0;

    for (const row of byId.values()) {
        if (row.isClaimed) continue;
        const remaining = Number(row.remainingEscrowFloat ?? 0);
        if (!Number.isFinite(remaining) || remaining <= 0) continue;
        totalAmount += remaining;
        paymentCount += 1;
        if (row.isDisputeRaised) disputedCount += 1;
    }

    return { totalAmount, paymentCount, disputedCount };
};

export const fetchLockedPaymentHistoryRows = async ({
    walletAddress,
    role,
    readContractData,
    contractAddress,
    contractAbi,
    tokenList,
}) => {
    if (!walletAddress) return [];

    const payments = await fetchLockedPayments(
        readContractData,
        contractAddress,
        contractAbi,
        walletAddress,
        role,
    );

    const rows = [];
    for (const paymentData of payments) {
        const tokenAddress = paymentData.tokenAddress ?? paymentData.token;
        const tokenInfo = tokenList.find(
            (token) =>
                String(token.address).toLowerCase() ===
                String(tokenAddress || '').toLowerCase(),
        );
        const row = mapLockedPaymentToHistoryRow(
            paymentData,
            paymentData.chainIndex,
            tokenInfo,
        );
        if (row) rows.push(row);
    }

    return rows;
};
